import { Effect, Reducer } from 'umi';
import {
  addDashboardPath,
  addFollowBatchGame,
  getCustomizedMetricsList,
  getExplainInfo,
  getFollowViewd,
  getGameListList,
  getLeftMenuList,
  getMarqueeInfos,
  getMetricsList,
  getRecentViewd,
  getDashboardRainbowInfo,
} from '@/pages/dashboard/services/dashboard';
import { storage } from '@/utils/utils';
import { unique } from '@/pages/dashboard/utils/utils';
import { MetricsItemType, RecentViewItemType } from '@/pages/dashboard/types/dashboard';
import {
  BUSINESS_TYPE_MYDASHBOARD,
  DB_DASHBOARD_LOCAL_CURRENT_GAME_KEY,
  DEFAULT_WIDTH,
  FOLDER_NAME_FAVORITES,
  NIKKE_COMPETITOR_GAME,
  NIKKE_MENU_MSG,
  PAGE_TYPE_OUT_PATH,
  RAINBOW_FLAG_CONFIG,
} from '@/pages/dashboard/_config/constant';
import { formatGameList } from './_config/_utils';
import { IS_GLOBAL } from '@home/_config/config';

export const namespace = 'dashboard';
export interface DashboardModelStateType {
  // 左侧内容层遮罩，用来遮挡iframe
  showContentMask: false;
  // 左侧菜单宽度
  leftMenuWidth: number;
  rawGameList: Array<any>;
  // 游戏列表
  gameList: Array<any>;
  // 当前选中的游戏
  currentGame: null | object;
  // 菜单列表
  menuList: null | Array<any>;
  folderList: Array<any>;
  dashboardList: Array<any>;
  // 前端记录保存需要展示New标记的菜单数据
  showNewMenusMap: Map<string, Set<string>>;
  // 当前菜单
  currentMenu: null | object;
  // 搜索指标
  metricsList: Array<MetricsItemType>;
  // 特性报表搜索指标
  customizedMetricsList: Array<MetricsItemType>;
  // 最近浏览的游戏
  recentViewList: Array<RecentViewItemType>;
  // 指标说明
  explainInfo: null | object;
  // 顶部的跑马灯通知信息.
  marqueeInfos: object;
  noAllGamePermission: boolean; // 没有所有游戏权限
  refreshEvent: object;
  // 指标信息
  metricsInfo: null | object;
  // 指标信息是否已加载完成（成功或失败）
  metricsInfoReady: boolean;
  // Agent 入口配置（七彩石 agent_info）
  agentInfoConfig: Record<string, any>;
}

export interface DashboardModelType {
  namespace: string;
  state: DashboardModelStateType;
  effects: {
    query: Effect;
    getGameList: Effect;
    // 切换游戏
    changeGame: Effect;
    getLeftMenuList: Effect;
    getMetricsList: Effect;
    getCustomizedMetricsList: Effect;
    getRecentViewd: Effect;
    getRecentAndFollowViewd: Effect;
    getFollowViewd: Effect;
    batchSaveFollow: Effect;
    addFollowGame: Effect;
    addFollowBatchGame: Effect;
    removeFollowGame: Effect;
    // 获取指标注释
    getExplainInfo: Effect;
    getMarqueeInfos: Effect;
    // 取消菜单上New的标识
    cancelMenuNewStatusLog: Effect;
    // 获取指标信息
    getMetricsInfo: Effect;
    // 获取 Agent 入口配置
    getAgentInfoConfig: Effect;
  };
  reducers: {
    save: Reducer<DashboardModelStateType>;
    // 启用 immer 之后
    // save: ImmerReducer<IndexModelState>;
    closeMarqueeInfos: Reducer<DashboardModelStateType>;
    changeRefreshEvent: Reducer<DashboardModelStateType>;
  };
  // subscriptions: { setup: Subscription };
}

const DashboardModel: DashboardModelType = {
  namespace,

  state: {
    showContentMask: false,
    leftMenuWidth: DEFAULT_WIDTH,
    rawGameList: [],
    gameList: [],
    currentGame: null,
    // null 代表未发起请求
    menuList: null,
    folderList: [],
    dashboardList: [],
    showNewMenusMap: new Map<string, Set<string>>(),
    currentMenu: null,
    metricsList: [],
    customizedMetricsList: [],
    recentViewList: [],
    // 浮窗指标
    explainInfo: {},
    // 顶部的跑马灯通知信息.
    marqueeInfos: {},
    noAllGamePermission: true, // 没有所有游戏权限
    // 用于判断页面内是否需要刷新事件详情列表的数据
    refreshEvent: {
      overview: false,
      realtime: false,
    },
    // 指标信息
    metricsInfo: {},
    metricsInfoReady: false,
    // Agent 入口配置
    agentInfoConfig: {},
  },

  effects: {
    *query({ payload }, { call, put }) { },
    // 获取游戏列表
    *getGameList({ payload }, { put, take, select }) {
      try {
        // @ts-ignore
        const result: any = yield getGameListList();
        const { main_game: gameList = [] } = result;
        const gameListData = formatGameList(gameList);
        // 判断是否一个游戏权限都没有
        const noAllGamePermission = gameListData.every(game =>
          !game?.is_access &&
          game?.game_List?.every((subGame: { is_access: boolean; }) => !subGame?.is_access)
        );

        yield put({
          type: "save", payload: {
            rawGameList: gameListData || [],
            noAllGamePermission,
          }
        });
      } catch (e) {
        yield put({
          type: "save", payload: {
            rawGameList: [],
            noAllGamePermission: true,
          }
        });
      }
    },

    // 切换游戏
    *changeGame({ payload, callback }, { put, select }) {
      const { gameCode } = payload;
      const { gameList = [] } = yield select((state: any) => state[namespace]) || {};
      const { userInfo = null } = yield select((state: any) => state.home) || {};
      const currentGame = findGame(gameCode, gameList);
      if (currentGame && userInfo) {
        storage.setItem(DB_DASHBOARD_LOCAL_CURRENT_GAME_KEY, {
          userId: userInfo.userid,
          code: currentGame?.game_code,
        });
      }
      yield put({
        type: "save", payload: {
          currentGame,
          menuList: null,
          folderList: [],
          dashboardList: [],
          customizedMetricsList: [],
          currentMenu: null
        }
      });
      callback?.();
    },

    // 获取左侧菜单
    *getLeftMenuList({ payload }, { put, select }) {
      // const { currentGame } = payload;
      const { currentGame } = yield select((state: any) => state[namespace]);
      const { game_code: gameCode = '', studio = '', role_id: roleId = '', role_type: roleType = '', third_party_game: thirdPartyGame } = currentGame || {};
      // @ts-ignore
      const result: any = currentGame?.is_access ? yield getLeftMenuList({
        user_id: '101',
        game_code: gameCode,
        studio_code: studio,
        role_id: roleId,
        type: roleType,
        third_party_game: thirdPartyGame
      }) : { menu_list: [] };
      const { menuList = [], folderList = [], subFolderList = [], dashboardList = [], showNewMenusMap = new Map() } = handleMenuData(result?.menu_list || [], gameCode) as any;

      yield put({
        type: "save", payload: {
          currentMenu: null,
          menuList,
          folderList,
          subFolderList,
          dashboardList,
          showNewMenusMap,
        }
      });
    },

    // 获取搜索指标
    *getMetricsList({ payload }, { put }) {
      const { language } = payload;
      try {
        const result: MetricsItemType[] = yield getMetricsList(language);
        yield put({
          type: "save", payload: {
            metricsList: result
          }
        });
      } catch (error) {
        console.log(error);
      }
    },

    // 获取特性搜索指标
    *getCustomizedMetricsList({ payload }, { put }) {
      const { gameCode } = payload;
      try {
        const result: MetricsItemType[] = yield getCustomizedMetricsList(gameCode);
        yield put({
          type: "save", payload: {
            customizedMetricsList: result
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
    // 获取最近浏览和收藏的游戏
    *getRecentAndFollowViewd({ payload }, { put, select }) {
      const recentList = yield getRecentViewd();
      const followList = yield getFollowViewd(payload);
      const { gameList } = yield select((state: any) => state[namespace]);
      const filterGameList = gameList?.map((game: any) => {
        const followTarget = followList?.find((followItem: any) => followItem.game_code === game.game_code);

        return {
          ...game,
          isFollow: Boolean(followTarget),
          followId: followTarget?.id,
          followSort: followTarget?.sort
        };
      });

      const nextFollowList = followList?.map((followGame: any) => (
        filterGameList?.find((game: any) => game.game_code === followGame.game_code)
      ))?.filter((game: any) => game && game?.isFollow);

      const nextRecentList = recentList?.map((recentGame: any) =>
        filterGameList?.find((game: any) => {
          // GLOBAL环境最近浏览过滤无权限游戏
          if (!game.is_access && IS_GLOBAL) return false; 
          // 用子版本的 game_code 做对比，不然顺序会有错误
          return (
            game?.game_List?.some((item: any) => item.game_code === recentGame.game_code) ||
            game.game_code === recentGame.game_code
          );
        })
      )?.filter((item: any) => item); // 过滤掉不存在的游戏（后端脏数据）    

      // 按照game_code去重
      yield put({
        type: "save", payload: {
          followList: nextFollowList,
          recentList: unique(nextRecentList, 'game_code'),
        }
      });

      // recent 或者 follow 有一项则可以展示我的游戏，否则跳转到所有游戏
      return nextFollowList?.length > 0 || nextRecentList?.length > 0;
    },
    *batchSaveFollow({ payload }, { put, select }) {
      yield addFollowBatchGame({
        data: payload.data
      });
      const followList = yield getFollowViewd({
        game_code: payload.game_code
      });
      const { gameList } = yield select((state: any) => state[namespace]);
      const filterGameList = gameList?.map((game: any) => {
        const followTarget = followList?.find((followItem: any) => followItem.game_code === game.game_code);
        return {
          ...game,
          isFollow: Boolean(followTarget),
          followId: followTarget?.id,
          followSort: followTarget?.sort
        };
      });

      // 批量更新完后及时更新收藏列表
      yield put({
        type: "save", payload: {
          followList: followList?.map((followGame: any) => (
            filterGameList?.find((game: any) => game.game_code === followGame.game_code)
          ))?.filter((game: any) => game && game?.isFollow)
        }
      });
    },
    // 获取指标说明
    *getExplainInfo({ payload }, { put }) {
      try {
        const result: MetricsItemType[] = yield getExplainInfo(payload.gameCode);
        const obj: any = {};
        if (result && result?.length > 0) {
          let bool = false;
          result.forEach((i: any) => {
            if (!bool) bool = true;

            obj[i.key] = {
              value_en: i?.value_en,
              value_cn: i?.value_cn
            };
          });
        }
        yield put({
          type: "save", payload: {
            explainInfo: obj
          }
        });
      } catch (error) {
        console.log(error);
      }
    },

    // 获取顶部的跑马灯通知信息
    *getMarqueeInfos({ payload }, { put, select }) {
      try {
        const result: any = yield getMarqueeInfos({ game_code: payload.gameCode });
        const { marqueeInfos } = yield select((state: any) => state[namespace]);
        const data = result || [];
        const newInfos = data.reduce((acc, item) => {
          acc[item.page] = {
            isClose: marqueeInfos[item.page]?.isClose,
            contents: item.notice_info || []
          };
          return acc;
        }, {});
        if (JSON.stringify(newInfos) !== JSON.stringify(marqueeInfos)) {
          yield put({ type: 'save', payload: { marqueeInfos: newInfos } });
        }
      } catch (error) {
        console.log(error);
      }

    },

    // 用户点击了New标记的目录, 上报接口
    *cancelMenuNewStatusLog({ payload }, { put, select }) {
      try {
        const { gameCode, path, nameEn, folderNameEn } = payload;
        // GST 按过期时间强制展示的 NEW，点击/进入页面后仍保留，直到过期
        if (gameCode === 'gst' && checkDashboardIsNew(
          { name_en: nameEn, path, link: path },
          gameCode,
          { name_en: folderNameEn },
        )) {
          return;
        }
        // 上报请求
        if (path) {
          const result = yield addDashboardPath({ game_code: gameCode, path });
        }
        // 前端处理是否显示New的数据å
        const { showNewMenusMap } = yield select((state: any) => state[namespace]);
        const showNewPaths = showNewMenusMap.get(folderNameEn);
        showNewPaths?.delete(nameEn);
        if (showNewPaths && showNewPaths?.size === 0) showNewMenusMap.delete(folderNameEn);
        // 同步取消收藏目录下的New标记
        const favoriteShowNew = showNewMenusMap.get("Favorite Page");
        favoriteShowNew?.delete(nameEn);
        if (favoriteShowNew && favoriteShowNew?.size === 0) showNewMenusMap.delete("Favorite Page");
        yield put({ type: 'save', payload: { showNewMenusMap: new Map(showNewMenusMap) } });

      } catch (e) {
        // 上报失败的情况下, 前端不取消New的状态
        console.log("取消菜单New标识的上报接口错误:", e);
      }
    },

    // 获取指标信息
    *getMetricsInfo({ payload }, { put, select }) {
      const { gameCode } = payload;
      try {
        const { currentGame } = yield select((state: any) => state[namespace]);
        const gameFlags = (currentGame?.flags || '').split(',').map((f: string) => f.trim());
        const matchedFlag = Object.keys(RAINBOW_FLAG_CONFIG).find(key => gameFlags.includes(key));
        const rainbowFlag = matchedFlag ? RAINBOW_FLAG_CONFIG[matchedFlag] : undefined;
        const result: MetricsItemType[] = yield getDashboardRainbowInfo(gameCode, "metric_info", rainbowFlag);
        const metricsInfo = result?.reduce<Record<string, any>>((acc, item) => {
          try {
            acc[item.key] = JSON.parse(item.value);
          } catch (e) {}
          return acc;
        }, {});  
        yield put({
          type: "save", payload: {
            metricsInfo,
            metricsInfoReady: true,
          }
        });
      } catch (error) {
        console.log(error);
        yield put({
          type: "save", payload: {
            metricsInfoReady: true,
          }
        });
      }
    },

    *getAgentInfoConfig({ payload }, { put }) {
      const { gameCode } = payload;
      try {
        const result: MetricsItemType[] = yield getDashboardRainbowInfo(gameCode, 'agent_info');
        const agentInfoConfig = result?.reduce<Record<string, any>>((acc, item) => {
          try {
            acc[item.key] = JSON.parse(item.value);
          } catch (e) {}
          return acc;
        }, {});
        yield put({ type: 'save', payload: { agentInfoConfig } });
      } catch (error) {
        console.log(error);
        yield put({ type: 'save', payload: { agentInfoConfig: {} } });
      }
    },
  },

  reducers: {
    save(state: DashboardModelStateType, action: any) {
      return {
        ...state,
        ...action.payload,
      };
    },
    closeMarqueeInfos(state: DashboardModelStateType, { payload }: any) {
      if (!state.marqueeInfos[payload]) state.marqueeInfos[payload] = {};
      state.marqueeInfos[payload].isClose = true;
      return {
        ...state,
      };
    },
    changeRefreshEvent(state: DashboardModelStateType, action: any) {
      return {
        ...state,
        refreshEvent: {
          ...state.refreshEvent,
          ...action.payload,
        },
      };
    }
  }
};

export default DashboardModel;

// 根据路由查询game
export const findGame = (gameCode: string, gameList: Array<any>): any => {
  let currentGame = null;
  gameList.some(item => {
    const { game_List: subGameList } = item;
    if (subGameList.length > 0) {
      // 先从子列表查找
      return subGameList.some((subItem: any) => {
        if ((gameCode && subItem?.game_code === gameCode) || (!gameCode && subItem?.is_access)) {
          currentGame = {
            ...subItem,
            // sameLevelList: subGameList
          };
          return true;
        }
        return false;
      });
    } 
    if ((gameCode && item?.game_code === gameCode) || (!gameCode && item?.is_access)) {
      currentGame = {
        ...item,
        // sameLevelList: subGameList
      };
      return true;
    }
    return false;
    
  });
  return currentGame || gameList?.[0]?.game_List?.[0] || gameList?.[0] || null;
};

const GST_NEW_EXPIRE_UTC_MS = Date.UTC(2026, 7, 31, 0, 0, 0);

const checkDashboardIsNew = (dashboard: any, gameCode: string, folder: any) => {
  if (gameCode === 'dune') {
    return folder?.name_en === 'Tech Metrics & Stability'
      || ['Real-time Dashboard', 'Real-time Tech Performance'].includes(dashboard.name_en);
  }
  if (gameCode === 'gst') {
    const path = String(dashboard?.path || dashboard?.link || '').replace(/^\/+/, '');
    return Date.now() < GST_NEW_EXPIRE_UTC_MS
      && (
        ['Real-time Dashboard', 'Real-time Opinions'].includes(dashboard.name_en)
        || path === 'performance/overall'
        || path.startsWith('performance/overall/')
      );
  }
  return false;
};

const handleMenuData = (menuList: Array<any>, gameCode: string): object => {
  const dashboardList = [] as any;
  const folderList = [] as any;
  const subFolderList = [] as any;
  const showNewMenusMap = new Map();
  menuList.forEach((menu => {
    menu.id = menu?.u_id || '';
    menu.parent_id = menu?.u_parent_id || '';
    (menu?.folder_list || []).forEach((folder: any) => {
      folder.id = folder?.u_id || '';
      folder.parent_id = folder?.u_parent_id || '';
      // 屏蔽Favorites目录，该目录表示收藏的报表，并不不能添加实体报表
      const { business_type: businessType, name_en: nameEn } = folder;
      if (!(businessType === BUSINESS_TYPE_MYDASHBOARD && nameEn === FOLDER_NAME_FAVORITES)) {
        folderList.push(folder);
      }
      (folder?.dashoboard_list || []).forEach((dashboard: any) => {
        dashboard.link = dashboard?.link || '';
        dashboard.parent_name_en = folder.name_en;
        dashboard.parent_name_cn = folder.name_cn;
        dashboard.id = dashboard?.u_id || '';
        dashboard.parent_id = dashboard?.u_parent_id || '';
        dashboard.menu_id =  menu?.u_id || '';
        if (dashboard?.menu_type === '2') {
          // 判断菜单上是否需要展示New
          if (dashboard.new_flag || checkDashboardIsNew(dashboard, gameCode, folder)) {
            const paths = showNewMenusMap.get(folder?.name_en) || new Set();
            showNewMenusMap.set(folder?.name_en, paths.add(dashboard?.name_en));
          }
          dashboard.folder_id = folder?.u_id || '';
          dashboardList.push({
            ...dashboard,
            parent_name_en: folder?.name_en,
            parent_name_cn: folder?.name_cn,
            menu_id: menu?.u_id || '',

          });
        } else if (dashboard?.menu_type === '3') {
          subFolderList.push(dashboard);
          dashboard?.dashoboard_list?.forEach((subDashboard: any) => {
            subDashboard.link = subDashboard?.link || '';
            // 判断子目录是否需要展示New
            if (subDashboard.new_flag || checkDashboardIsNew(subDashboard, gameCode, folder)) {
              const paths = showNewMenusMap.get(dashboard?.name_en) || new Set();
              const folderPaths = showNewMenusMap.get(folder?.name_en) || new Set();
              showNewMenusMap.set(folder?.name_en, folderPaths.add(subDashboard?.name_en));
              showNewMenusMap.set(dashboard?.name_en, paths.add(subDashboard?.name_en));
            }
            subDashboard.id = subDashboard?.u_id || '';
            subDashboard.parent_id = subDashboard?.u_parent_id || '';
            subDashboard.parent_name_en = dashboard?.name_en;
            subDashboard.parent_name_cn = dashboard.name_cn;
            subDashboard.folder_id = folder?.u_id || '';

            dashboardList.push({
              ...subDashboard,
              parent_name_en: dashboard?.name_en,
              parent_name_cn: dashboard?.name_cn,
              menu_id: menu?.u_id || '',
            });
          });
        }

        if (folder?.name_en?.toLowerCase() === 'competitor' && NIKKE_COMPETITOR_GAME?.includes(gameCode)) {
          const isHaveOutpath = folder.dashoboard_list?.filter((item: any) => item?.page_type === PAGE_TYPE_OUT_PATH)?.length > 0;
          if (isHaveOutpath) {
            folder.dashoboard_list = [...(folder?.dashoboard_list || [])];
          } else {
            folder.dashoboard_list = [...(folder?.dashoboard_list || []), ...NIKKE_MENU_MSG];
          }

        }

        // 判断二级目录是否默认展开
        if (!folder.isDefaultOpen) {
          folder.isDefaultOpen = menu?.business_type === "game" && (dashboard?.path?.includes("/overview/") || dashboard?.path?.includes("/realtime/"));
        }
      });
    });
  }));

  return {
    menuList,
    folderList,
    subFolderList,
    dashboardList,
    showNewMenusMap
  };
};
